import React, { useState, useEffect } from "react";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import { URL as NavigationURL } from "../../../constants/navigation";
import { Step, GroupStatistics } from "../types";
import { StepContainer } from "./StepContainer";
import Api from "../../../services/Api";
import useAuth from "../../login/AuthContext";
import { useSnackbar } from "../../../hooks/useSnackbar";

interface ReviewStepProps {
  setCurrentStep: (step: number) => void;
  selectedMatchingRound: number | null;
  selectedNetworkL1Id: number;
  onGroupStatisticsUpdate?: (stats: GroupStatistics) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  setCurrentStep,
  selectedMatchingRound,
  selectedNetworkL1Id,
  onGroupStatisticsUpdate
}) => {
  const { getAuthToken } = useAuth();
  const { showErrorSnackbar } = useSnackbar();
  const [groupStats, setGroupStats] = useState<GroupStatistics>({
    fullGroups: 0,
    partialGroups: 0,
    singleResidents: 0
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchGroupStatistics = async () => {
      if (!selectedMatchingRound || !selectedNetworkL1Id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Try to get statistics from the home API first
        const homeResult = await Api.getHome(selectedNetworkL1Id, await getAuthToken());

        if (homeResult && !homeResult.error && homeResult.response?.statsBySubNetwork) {
          // Convert selectedMatchingRound (l2Id) to string key for API response access
          const subnetworkStats = homeResult.response.statsBySubNetwork[String(selectedMatchingRound)];

          if (subnetworkStats) {
            // Check if the required fields are available in the API response
            const hasRequiredFields = 'fullyMatchedGroups' in subnetworkStats &&
                                     'partiallyMatchedGroups' in subnetworkStats &&
                                     'singleUsers' in subnetworkStats;

            if (hasRequiredFields) {
              // Use the actual statistics from the home API response
              const fullyMatchedGroups = subnetworkStats.fullyMatchedGroups || 0;
              const partiallyMatchedGroups = subnetworkStats.partiallyMatchedGroups || 0;
              const singleUsers = subnetworkStats.singleUsers || 0;

              const stats = {
                fullGroups: Math.max(0, fullyMatchedGroups),
                partialGroups: Math.max(0, partiallyMatchedGroups),
                singleResidents: Math.max(0, singleUsers)
              };

              setGroupStats(stats);

              // Update parent component with the statistics
              if (onGroupStatisticsUpdate) {
                onGroupStatisticsUpdate(stats);
              }
            } else {
              // Required fields are missing from API response
              showErrorSnackbar("Group statistics not available from backend");
              const stats = {
                fullGroups: 0,
                partialGroups: 0,
                singleResidents: 0
              };

              setGroupStats(stats);

              // Update parent component with the statistics
              if (onGroupStatisticsUpdate) {
                onGroupStatisticsUpdate(stats);
              }
            }
          } else {
            // Fallback to the original API
            const result = await Api.getCurrrentMatchesOfMatchesGroup(
              selectedNetworkL1Id,
              selectedMatchingRound,
              await getAuthToken()
            );

            if (!result.error && result.result) {

              const stats = {
                fullGroups: 0,
                partialGroups: 0,
                singleResidents: 0
              };

              setGroupStats(stats);

              // Update parent component with the statistics
              if (onGroupStatisticsUpdate) {
                onGroupStatisticsUpdate(stats);
              }
            } else {
              showErrorSnackbar("Failed to load group statistics");
              const stats = {
                fullGroups: 0,
                partialGroups: 0,
                singleResidents: 0
              };

              setGroupStats(stats);

              // Update parent component with the statistics
              if (onGroupStatisticsUpdate) {
                onGroupStatisticsUpdate(stats);
              }
            }
          }
        } else {
          // Fallback to the original API
          const result = await Api.getCurrrentMatchesOfMatchesGroup(
            selectedNetworkL1Id,
            selectedMatchingRound,
            await getAuthToken()
          );

          if (!result.error && result.result) {
            const l3Matches = result.result || {};
            const totalMatches = Object.values(l3Matches).reduce((sum: number, count: any) => sum + (count || 0), 0);

            const stats = {
              fullGroups: Math.max(0, totalMatches),
              partialGroups: 0,
              singleResidents: 0
            };

            setGroupStats(stats);

            // Update parent component with the statistics
            if (onGroupStatisticsUpdate) {
              onGroupStatisticsUpdate(stats);
            }
          } else {
            showErrorSnackbar("Failed to load group statistics");
            const stats = {
              fullGroups: 0,
              partialGroups: 0,
              singleResidents: 0
            };

            setGroupStats(stats);

            // Update parent component with the statistics
            if (onGroupStatisticsUpdate) {
              onGroupStatisticsUpdate(stats);
            }
          }
        }
      } catch (error) {
        showErrorSnackbar("Failed to load group statistics");
        const stats = {
          fullGroups: 0,
          partialGroups: 0,
          singleResidents: 0
        };

        setGroupStats(stats);

        // Update parent component with the statistics
        if (onGroupStatisticsUpdate) {
          onGroupStatisticsUpdate(stats);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchGroupStatistics();
  }, [ selectedNetworkL1Id]);

  const rightPanel = (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="subtitle1" fontWeight="500" mb={3} sx={{ fontSize: '16px' }}>
        Export List
      </Typography>

      <Box sx={{ width: '100%', maxWidth: '100%', mb: 4 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'nowrap', alignItems: 'stretch' }}>
            <Box sx={{ flex: 1, display: 'flex' }}>
              <Box sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: 1,
                height: '200px',
                width: '100%',
                minWidth: '280px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                    {groupStats.fullGroups} Full Roommate Groups
                  </Typography>
                  <Box sx={{
                    display: 'inline-block',
                    bgcolor: '#D1FAE5',
                    color: '#065F46',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    mb: 3
                  }}>
                    Exportable
                  </Box>
                </Box>
              <Box sx={{ pb: '20px' }}>
                <Typography
                  onClick={() => window.open(NavigationURL.ROOMMATE_MATCHING_REPORT, '_blank')}
                  sx={{
                    color: '#2167e2',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  View
                  <span style={{ fontSize: '16px' }}>›</span>
                </Typography>
              </Box>
            </Box>
          </Box>

            <Box sx={{ flex: 1, display: 'flex' }}>
              <Box sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: 1,
                height: '200px',
                width: '100%',
                minWidth: '280px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                    {groupStats.partialGroups} Partial Roommate Groups
                  </Typography>
                  <Box sx={{
                    display: 'inline-block',
                    bgcolor: '#D1FAE5',
                    color: '#065F46',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    mb: 3
                  }}>
                    Exportable
                  </Box>
                </Box>
              <Box sx={{ pb: '20px' }}>
                <Typography
                  onClick={() => window.open(NavigationURL.USERS_MATCH, '_blank')}
                  sx={{
                    color: '#2167e2',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  Complete Matching
                  <span style={{ fontSize: '16px' }}>›</span>
                </Typography>
              </Box>
            </Box>
          </Box>

            <Box sx={{ flex: 1, display: 'flex' }}>
              <Box sx={{
                p: 3,
                border: '1px solid #FECACA',
                borderRadius: 1,
                height: '200px',
                width: '100%',
                minWidth: '280px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 500, mb: 1 }}>
                    {groupStats.singleResidents} Single Residents
                  </Typography>
                  <Box sx={{
                    display: 'inline-block',
                    bgcolor: '#FECACA',
                    color: '#991B1B',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    mb: 3
                  }}>
                    Not Exportable
                  </Box>
                </Box>
              <Box sx={{ pb: '20px' }}>
                <Typography
                  onClick={() => window.open(NavigationURL.USERS_MATCH, '_blank')}
                  sx={{
                    color: '#B91C1C',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  Resolve
                  <span style={{ fontSize: '16px' }}>›</span>
                </Typography>
              </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box mt={3} sx={{ width: '100%', pb: '20px', display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setCurrentStep(Step.SELECT_MATCHING_ROUND)}
          sx={{
            textTransform: 'none',
            borderColor: '#1e3a5f',
            color: '#1e3a5f',
            '&:hover': {
              borderColor: '#1e3a5f',
              bgcolor: 'rgba(30, 58, 95, 0.04)'
            },
            fontSize: '14px',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 500
          }}
        >
          Go Back
        </Button>
        <Button
          variant="contained"
          onClick={() => setCurrentStep(Step.CONFIRM_EXPORT)}
          sx={{
            textTransform: 'none',
            bgcolor: '#2167e2',
            '&:hover': { bgcolor: '#1a52b3' },
            fontSize: '14px',
            padding: '10px 20px',
            borderRadius: '8px',
            boxShadow: 'none',
            fontWeight: 500
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );

  return (
    <StepContainer rightPanel={rightPanel} />
  );
};
